import React from 'react';
import { HealthHxItemType, formatDate, healthHxTabs } from '~/util';

interface HistoryRowProps {
    history: HealthHxItemType;
    type: string;
}
export function HistoryRow({ history, type }: HistoryRowProps) {
    function getParts() {
        switch (type) {
            case healthHxTabs.labs:
                return {
                    icon: 'fa-flask',
                    title: history.labResultType,
                    year: (history.labTestDate as string).substring(0, 4),
                    description: [
                        {
                            label: 'Description',
                            value: history.labTestName,
                        },
                        {
                            label: 'Results',
                            value: history.labTestResult,
                        },
                    ],
                    dates: [
                        {
                            label: 'Test Date',
                            value: formatDate(history.labTestDate),
                        },
                    ],
                };
            case healthHxTabs.meds:
                return {
                    icon: 'fa-medkit',
                    title: history.drugName,
                    year: (history.fillEndDate as string).substring(0, 4),
                    description: [
                        {
                            label: 'Quantity',
                            value: history.quantity,
                        },
                        {
                            label: 'Days Supply',
                            value: history.daysSupply,
                        },
                    ],
                    dates: [
                        {
                            label: 'Fill Start Date',
                            value: formatDate(history.fillStartDate),
                        },
                        {
                            label: 'Fill End Date',
                            value: formatDate(history.fillEndDate),
                        },
                    ],
                };
            case healthHxTabs.specVisits:
                return {
                    icon: 'fa-user-md',
                    title: history.claimSpecialtyClass,
                    year: (history.dos as string).substring(0, 4),
                    description: [
                        {
                            label: 'Provider',
                            value: history.providerFullName,
                        },
                        {
                            label: 'Procedure Code',
                            value: history.serviceProcedureCode,
                        },
                        {
                            label: 'Details',
                            value: history.procedureCodeDesc,
                        },
                    ],
                    dates: [
                        {
                            label: 'Date of Service',
                            value: formatDate(history.dos),
                        },
                    ],
                };
            case healthHxTabs.diagnosis:
                return {
                    icon: 'fa-stethoscope',
                    title: history.diagCode,
                    year: (history.dateOfService as string).substring(0, 4),
                    description: [
                        {
                            label: 'ICD (Sum)',
                            value: history.icdSum,
                        },
                        {
                            label: 'Description',
                            value: history.diagnosisDescription,
                        },
                    ],
                    dates: [
                        {
                            label: 'Date of Service',
                            value: formatDate(history.dateOfService),
                        },
                    ],
                };
            case healthHxTabs.vitals:
                return {
                    icon: 'fa-heartbeat',
                    title: formatDate(history.vitalDate),
                    year: (history.vitalDate as string).substring(0, 4),
                    description: [
                        { label: 'Heart Rate', value: history.heartRate },
                        { label: 'Blood Pressure', value: history.bloodPressure },
                        { label: 'Respiratory Rate', value: history.respiratoryRate },
                        { label: 'Temp (F)', value: history.temp },
                        { label: 'BMI', value: history.bmi },
                        { label: 'Height (in)', value: history.height },
                        { label: 'Weight (lbs)', value: history.weight },
                        { label: 'Waist Circumference (in)', value: history.waistCircumference },
                    ],
                    dates: [
                        {
                            label: 'Vitals Date',
                            value: formatDate(history.vitalDate),
                        },
                    ],
                };
            case healthHxTabs.inptOutpt:
                return {
                    icon: 'fa-user-md',
                    title: history.facility,
                    year: (history.admitDate as string).substring(0, 4),
                    description: [
                        { label: 'Provider Specialty', value: history.providerSpecialty },
                        { label: 'Diagnosis Code', value: history.diagnosisCode },
                        { label: 'DRG', value: history.drg },
                    ],
                    dates: [
                        {
                            label: 'Admit Date',
                            value: formatDate(history.admitDate),
                        },
                        {
                            label: 'Discharge Date',
                            value: history.dischargeDate ? formatDate(history.dischargeDate) : null,
                        },
                    ],
                };
            default:
                return {
                    icon: '',
                    title: '',
                    year: '',
                    description: [],
                    dates: [{ label: '', value: '' }],
                };
        }
    }
    const { icon, title, year, description, dates } = getParts();

    return (
        <div className="card border-info mb-2 note-card">
            <div className="card-header card-header-ignore-margin">
                <div className="row">
                    <div className="col-9">
                        <div className="pull-left">
                            <i className={`fa-solid me-1 ${icon}`} />
                            <span className="fw-bold">{title}</span>
                        </div>
                    </div>
                    <div className="col-3">
                        <div className="pull-right">
                            <span className="badge badge-outline-secondary audit-report-year">
                                {year}
                            </span>
                        </div>
                    </div>
                </div>
            </div>
            <div className="card-body note-text">
                <div className="row">
                    <div className={type === healthHxTabs.meds ? 'col-6' : 'col-9'}>
                        {description.map((x) => (
                            <React.Fragment key={x.label}>
                                <div
                                    className={`me-3 ${
                                        type === healthHxTabs.specVisits && x.label === 'Details'
                                            ? 'd-block'
                                            : 'd-inline-block'
                                    }`}
                                >
                                    <span className="fw-bold">{x.label}:</span> {x.value}
                                </div>
                                {type === healthHxTabs.vitals && x.label === 'Temp (F)' && <br />}
                            </React.Fragment>
                        ))}
                    </div>
                    <div className={`${type === healthHxTabs.meds ? 'col-6' : 'col-3'} text-end`}>
                        {dates.map((date) => (
                            <div key={date.label} className="me-3 d-inline-block">
                                <span className="fw-bold">{date.label}</span>: {date.value}
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}
